import sc from '@polarsteps/polarsteps-shared-core';
import useSWR from 'swr';
import { LOCATION_IQ_KEY } from 'constants/env';
import { get } from 'utils/http';
import { location1toLocation2 } from 'utils/location-utils';
import {
    mapAutocompleteLocationToPSLocationWithBB,
    mapGeocodeLocationToPSLocation,
} from 'utils/map-location';

import type { com } from '@polarsteps/polarsteps-shared-core';

type SearchMode = 'places' | 'countries';

export function useSearchByName(
    query: string,
    searchMode: SearchMode = 'places',
) {
    const geocoder = LOCATION_IQ_KEY
        ? sc.polarstepsCore().createGeocoder(LOCATION_IQ_KEY)
        : null;

    const { data, isValidating, error } = useSWR(
        getKeyShare(query, searchMode),
        async () => {
            if (!geocoder) {
                return null;
            }
            const AutocompleteParameters =
                sc.com.polarsteps.shared.service.AutocompleteParameters;
            const params =
                searchMode === 'countries'
                    ? AutocompleteParameters.forCountrySearch(query)
                    : AutocompleteParameters.forStepSearchWithViewBox(
                          query,
                          null,
                      );
            const req = geocoder.autocompleteRequest(params);
            const searchParams = new URLSearchParams();
            req.parameters
                .asJsReadonlyArrayView()
                .forEach((param) => searchParams.set(param.key, param.value));
            const url = new URL(`${req.url}?${searchParams.toString()}`);
            const result = await get(url.toString(), {
                credentials: 'omit',
            });
            const parsed = geocoder.parseAutocompleteResult(
                JSON.stringify(result),
                params,
            );
            return parsed.asJsReadonlyArrayView();
        },
        { revalidateOnFocus: false },
    );

    return {
        locationsByName: data,
        isLoading: isValidating && !data && !error,
        isValidating,
        error,
    };
}

function getKeyShare(
    query: string | null | undefined,
    searchMode: SearchMode = 'places',
): string | null {
    if (!query || query.length < 3) {
        return null;
    }
    return `locationiqSearchByName/${searchMode}/${query}`;
}

async function searchSpotByName(
    query: string,
    preferredBounds?: Bounds,
): Promise<PSLocation2 | null> {
    if (!LOCATION_IQ_KEY || query.length < 3) {
        return null;
    }
    const geocoder = sc.polarstepsCore().createGeocoder(LOCATION_IQ_KEY);
    const AutocompleteParameters =
        sc.com.polarsteps.shared.service.AutocompleteParameters;
    const viewBox = preferredBounds ? boundsToViewBox(preferredBounds) : null;
    const params = AutocompleteParameters.forStepSearchWithViewBox(
        query,
        viewBox,
    );
    const req = geocoder.autocompleteRequest(params);
    const searchParams = new URLSearchParams();
    req.parameters
        .asJsReadonlyArrayView()
        .forEach((param) => searchParams.set(param.key, param.value));
    const url = new URL(`${req.url}?${searchParams.toString()}`);
    const result = await get(url.toString(), { credentials: 'omit' });
    const parsed = geocoder.parseAutocompleteResult(
        JSON.stringify(result),
        params,
    );
    const locations = parsed.asJsReadonlyArrayView();
    if (!locations.length) {
        return null;
    }
    const first = mapAutocompleteLocationToPSLocationWithBB(locations[0]);
    if (!first) {
        return null;
    }
    const processedSearch = location1toLocation2(first);
    return {
        ...processedSearch,
        single_line: locations[0].presentation.fullResult,
    };
}

async function searchSpotByLatLon(
    lat: number,
    lon: number,
): Promise<PSLocation2> {
    if (!LOCATION_IQ_KEY) {
        return fallbackPSLocation2(lat, lon);
    }
    try {
        const geocoder = sc.polarstepsCore().createGeocoder(LOCATION_IQ_KEY);
        const params = sc.com.polarsteps.shared.service.ReverseGeocodingParameters.forLocation(
            lat,
            lon,
            true,
        );
        const req = geocoder.reverseGeocodeRequest(params);
        const searchParams = new URLSearchParams();
        req.parameters
            .asJsReadonlyArrayView()
            .forEach((param) => searchParams.set(param.key, param.value));
        const url = new URL(`${req.url}?${searchParams.toString()}`);
        const result = await get<com.polarsteps.shared.domain.userdata.GeocodeLocation>(
            url.toString(),
            { credentials: 'omit' },
        );
        if (!result?.address) {
            throw new Error('searchSpotByLatLon: no result');
        }
        const parsed = geocoder.parseReverseGeocodeResult(
            JSON.stringify(result),
            params,
        );
        if (!parsed) {
            throw new Error('searchSpotByLatLon: could not parse result');
        }
        const psLocation = mapGeocodeLocationToPSLocation(parsed);
        const processedSearch = location1toLocation2(psLocation);
        return {
            ...processedSearch,
            lat,
            lon,
            single_line:
                parsed.presentation?.fullResult ?? parsed.locality ?? 'Unknown',
        };
    } catch {
        return fallbackPSLocation2(lat, lon);
    }
}

function fallbackPSLocation2(lat: number, lon: number): PSLocation2 {
    return {
        lat,
        lon,
        source: '',
        locality: '',
        administrative_area: '',
        country: '',
        country_code: '',
        single_line: 'Unknown',
    };
}

export const searchSpot = async (
    query: string,
    preferredBounds?: Bounds,
): Promise<PSLocation2 | null> => {
    if (!query) {
        return null;
    }

    const isLatLon = query.match(
        /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/,
    );
    if (isLatLon) {
        const [lat, lon] = query
            .replace(' ', '')
            .split(',')
            .map((string) => parseFloat(string));
        return searchSpotByLatLon(lat, lon);
    }
    return searchSpotByName(query, preferredBounds);
};

type Bounds = [[number, number], [number, number]];

function boundsToViewBox(bounds: Bounds): com.polarsteps.shared.domain.ViewBox {
    const minLon = Math.min(bounds[0][0], bounds[1][0]);
    const maxLon = Math.max(bounds[0][0], bounds[1][0]);
    const minLat = Math.min(bounds[0][1], bounds[1][1]);
    const maxLat = Math.max(bounds[0][1], bounds[1][1]);
    const Coordinate = sc.com.polarsteps.shared.domain.Coordinate;
    const ViewBox = sc.com.polarsteps.shared.domain.ViewBox;
    return new ViewBox(
        new Coordinate(minLat, minLon),
        new Coordinate(maxLat, maxLon),
    );
}
